import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

// #7 typical lists (owner-configurable later); «Другое» → free text
const REASONS = ['Прокол камеры', 'Скрип тормозов', 'Проблема с цепью', 'Восьмёрка колеса', 'Переключатель', 'Плановое ТО', 'Другое']
const WORK_TYPES = ['Замена камеры', 'Замена покрышки', 'Регулировка тормозов', 'Регулировка переключателя', 'Замена цепи', 'Замена троса', 'Смазка', 'Подтяжка спиц', 'Замена педалей', 'Другое']

const STATUS_LABEL: Record<string, string> = { open: '🟢 Открыт', in_progress: '🟡 В работе', completed: '✅ Завершён', cancelled: '⚪ Отменён' }

interface WOListItem { id: string; status: string; reason: string; bikeCode: string | null; pointName: string; openedAt: string; totalCost: number | null; itemsCount: number; partsCount: number }
interface Bike { id: string; code: string }
interface Point { id: string; name: string }
interface Part { id: string; name: string; unit: string; stockByPoint?: { pointId: string; pointName: string; quantity: number }[] }
interface MechStockRow { partId: string; quantity: number; unit: string }

function errMsg(e: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (e as any)?.response?.data?.error?.message ?? 'Ошибка'
}

interface ShiftReport {
  shiftId: string; mechanicName: string; openedAt: string; closedAt: string | null
  ordersOpened: number; ordersCompleted: number; hours: number
  partsCost: number; laborCost: number; totalCost: number
  repairs: { id: string; bikeCode: string | null; reason: string; totalCost: number }[]
  parts: { name: string; unit: string; quantity: number; cost: number }[]
}

/** #9 Shift bar: open/close a mechanic shift and see the closing report.
 *  Purely for reporting — работать можно и без открытой смены. */
function ShiftBar() {
  const qc = useQueryClient()
  const [report, setReport] = useState<ShiftReport | null>(null)
  const { data } = useQuery<{ shift: { id: string; openedAt: string } | null }>({
    queryKey: ['mechanic-shift'],
    queryFn: () => api.get('/workorders/shift/current').then((r) => r.data),
  })
  const open = useMutation({
    mutationFn: () => api.post('/workorders/shift/open', {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mechanic-shift'] }),
  })
  const close = useMutation({
    mutationFn: () => api.post('/workorders/shift/close', {}).then((r) => r.data),
    onSuccess: (d) => { setReport(d.report); qc.invalidateQueries({ queryKey: ['mechanic-shift'] }) },
  })
  const shift = data?.shift

  return (
    <>
      <div className="flex items-center justify-between bg-white rounded-2xl p-3 border border-gray-100">
        <div className="text-sm">
          {shift
            ? <>Смена открыта <span className="text-gray-500">с {new Date(shift.openedAt).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span></>
            : <span className="text-gray-500">Смена не открыта</span>}
        </div>
        {shift
          ? <button disabled={close.isPending} onClick={() => close.mutate()} className="bg-gray-800 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Закрыть смену</button>
          : <button disabled={open.isPending} onClick={() => open.mutate()} className="bg-brand-600 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Открыть смену</button>}
      </div>

      {report && (
        <Overlay title="Отчёт за смену" onClose={() => setReport(null)}>
          <div className="space-y-2 text-sm">
            <p className="text-gray-500 text-xs">{report.mechanicName}</p>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Открыто нарядов" value={String(report.ordersOpened)} />
              <Stat label="Завершено" value={String(report.ordersCompleted)} />
              <Stat label="Часов работы" value={String(report.hours)} />
              <Stat label="Итого" value={`${report.totalCost} ₽`} />
            </div>
            {report.repairs.length > 0 && (
              <div>
                <p className="font-semibold mt-2">Завершённые ремонты</p>
                {report.repairs.map((r, i) => (
                  <div key={r.id} className="flex justify-between text-xs py-0.5">
                    <span>{i + 1}. {r.bikeCode ? `#${r.bikeCode}` : '—'} · {r.reason}</span>
                    <span className="text-gray-500">{r.totalCost} ₽</span>
                  </div>
                ))}
              </div>
            )}
            {report.parts.length > 0 && (
              <div>
                <p className="font-semibold mt-2">Списанные запчасти</p>
                {report.parts.map((p, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span>{p.name} × {p.quantity} {p.unit}</span>
                    <span className="text-gray-500">{p.cost} ₽</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Overlay>
      )}
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-2">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  )
}

export default function MechanicWorkOrdersPage() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ workOrders: WOListItem[] }>({
    queryKey: ['workorders'],
    queryFn: () => api.get('/workorders').then((r) => r.data),
  })

  return (
    <div className="p-4 space-y-4 max-w-xl mx-auto">
      <ShiftBar />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Заказ-наряды</h2>
        <button onClick={() => setCreating(true)} className="bg-brand-600 text-white text-sm font-semibold px-3 py-2 rounded-xl">+ Новый</button>
      </div>

      {isLoading && <p className="text-gray-500 text-sm">Загрузка…</p>}
      {data?.workOrders.length === 0 && <p className="text-gray-500 text-sm">Нарядов пока нет.</p>}

      <div className="space-y-2">
        {data?.workOrders.map((w) => (
          <button key={w.id} onClick={() => setOpenId(w.id)} className="w-full text-left bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <span className="font-medium">{w.bikeCode ? `#${w.bikeCode}` : 'Без велосипеда'} · {w.reason}</span>
              <span className="text-xs">{STATUS_LABEL[w.status] ?? w.status}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {w.pointName} · работ: {w.itemsCount} · запчастей: {w.partsCount}
              {w.totalCost != null && ` · ${w.totalCost} ₽`}
            </div>
          </button>
        ))}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); qc.invalidateQueries({ queryKey: ['workorders'] }); setOpenId(id) }} />}
      {openId && <DetailModal id={openId} onClose={() => { setOpenId(null); qc.invalidateQueries({ queryKey: ['workorders'] }) }} />}
    </div>
  )
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { data: bikesData } = useQuery<{ bikes: Bike[] }>({ queryKey: ['bikes'], queryFn: () => api.get('/bikes').then((r) => r.data) })
  const { data: pointsData } = useQuery<{ points: Point[] }>({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const [bikeSearch, setBikeSearch] = useState('')
  const [pointId, setPointId] = useState('')
  const [reason, setReason] = useState(REASONS[0])
  const [reasonCustom, setReasonCustom] = useState('')
  const [comment, setComment] = useState('')

  const bikes = bikesData?.bikes ?? []
  const matchedBike = bikes.find((b) => b.code === bikeSearch.trim())
  const bikeNotFound = bikeSearch.trim().length > 0 && !matchedBike

  const mut = useMutation({
    mutationFn: () => api.post('/workorders', {
      bikeId: matchedBike?.id ?? null,
      pointId,
      reason: reason === 'Другое' ? (reasonCustom.trim() || 'Другое') : reason,
      reasonCustom: reason === 'Другое' ? reasonCustom.trim() || null : null,
      comment: comment.trim() || null,
    }).then((r) => r.data),
    onSuccess: (d) => onCreated(d.workOrder.id),
  })

  return (
    <Overlay onClose={onClose} title="Новый заказ-наряд">
      <Field label="Велосипед — поиск по номеру (необязательно)">
        <input
          list="wo-bike-list"
          value={bikeSearch}
          onChange={(e) => setBikeSearch(e.target.value)}
          placeholder="Введите код велосипеда"
          className="input"
        />
        <datalist id="wo-bike-list">
          {bikes.map((b) => <option key={b.id} value={b.code} />)}
        </datalist>
        {matchedBike && <span className="text-[11px] text-green-600">✓ #{matchedBike.code}</span>}
        {bikeNotFound && <span className="text-[11px] text-amber-600">не найден — выберите из списка или оставьте пустым</span>}
      </Field>
      <Field label="Точка (склад/точка ремонта) *">
        <select value={pointId} onChange={(e) => setPointId(e.target.value)} className="input">
          <option value="">— выберите —</option>
          {pointsData?.points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Причина ремонта *">
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="input">
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      {reason === 'Другое' && (
        <Field label="Своя причина"><input value={reasonCustom} onChange={(e) => setReasonCustom(e.target.value)} className="input" placeholder="Опишите" /></Field>
      )}
      <Field label="Комментарий"><input value={comment} onChange={(e) => setComment(e.target.value)} className="input" /></Field>
      {mut.isError && <p className="text-red-500 text-xs">{errMsg(mut.error)}</p>}
      <button
        disabled={!pointId || bikeNotFound || mut.isPending}
        onClick={() => mut.mutate()}
        className="w-full bg-brand-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
      >
        {mut.isPending ? 'Создаю…' : 'Создать наряд'}
      </button>
    </Overlay>
  )
}

interface WODetail {
  id: string; status: string; reason: string; comment: string | null; bikeCode: string | null; pointName: string
  laborCost: number | null; partsCost: number | null; totalCost: number | null
  items: { id: string; workType: string; workTypeCustom: string | null; hours: number | null }[]
  parts: { id: string; partName: string; unit: string; quantity: number; totalCost: number }[]
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['workorder', id] })
  const { data } = useQuery<{ workOrder: WODetail }>({ queryKey: ['workorder', id], queryFn: () => api.get(`/workorders/${id}`).then((r) => r.data) })
  const wo = data?.workOrder
  const closed = wo?.status === 'completed' || wo?.status === 'cancelled'

  const [workType, setWorkType] = useState(WORK_TYPES[0])
  const [workTypeCustom, setWorkTypeCustom] = useState('')
  const [hours, setHours] = useState('')

  const { data: partsData } = useQuery<{ parts: Part[] }>({ queryKey: ['parts'], queryFn: () => api.get('/warehouse/parts').then((r) => r.data), enabled: !closed })
  const { data: ownStockData } = useQuery<{ stock: MechStockRow[] }>({ queryKey: ['mechanic-stock'], queryFn: () => api.get('/warehouse/mechanic-stock').then((r) => r.data), enabled: !closed })
  const [partId, setPartId] = useState('')
  const [partSource, setPartSource] = useState<'own' | 'point'>('own')  // #под-отчёт: по умолчанию из своего остатка
  const [partPointId, setPartPointId] = useState('')
  const [partQty, setPartQty] = useState('1')

  const ownQtyOf = (pid: string) => (ownStockData?.stock ?? []).find((s) => s.partId === pid)?.quantity ?? 0
  const selPart = partsData?.parts.find((p) => p.id === partId)
  const ownQty = ownQtyOf(partId)
  const pointsWithStock = (selPart?.stockByPoint ?? []).filter((s) => s.quantity > 0)
  const selectPart = (pid: string) => { setPartId(pid); setPartSource(ownQtyOf(pid) > 0 ? 'own' : 'point'); setPartPointId('') }

  const addWork = useMutation({
    mutationFn: () => api.post(`/workorders/${id}/items`, {
      workType: workType === 'Другое' ? (workTypeCustom.trim() || 'Другое') : workType,
      workTypeCustom: workType === 'Другое' ? workTypeCustom.trim() || null : null,
      hours: hours ? parseFloat(hours.replace(',', '.')) : null,
    }).then((r) => r.data),
    onSuccess: () => { setHours(''); setWorkTypeCustom(''); invalidate() },
  })
  const addPart = useMutation({
    mutationFn: () => api.post('/warehouse/writeoff', {
      partId,
      quantity: parseFloat(partQty.replace(',', '.')),
      workOrderId: id,
      ...(partSource === 'own' ? { fromOwnStock: true } : { pointId: partPointId }),
    }).then((r) => r.data),
    onSuccess: () => {
      setPartId(''); setPartQty('1'); setPartSource('own')
      invalidate()
      qc.invalidateQueries({ queryKey: ['mechanic-stock'] })
      qc.invalidateQueries({ queryKey: ['parts'] })
    },
  })
  const closeWo = useMutation({
    mutationFn: () => api.post(`/workorders/${id}/close`, {}).then((r) => r.data),
    onSuccess: () => onClose(),
  })

  return (
    <Overlay onClose={onClose} title={wo ? `${wo.bikeCode ? '#' + wo.bikeCode : 'Наряд'} · ${wo.reason}` : 'Наряд'}>
      {!wo ? <p className="text-gray-500 text-sm">Загрузка…</p> : (
        <div className="space-y-4">
          <div className="text-xs text-gray-500">{STATUS_LABEL[wo.status]} · {wo.pointName}{wo.comment ? ` · ${wo.comment}` : ''}</div>

          <section>
            <h4 className="font-semibold text-sm mb-1">Работы</h4>
            {wo.items.length === 0 && <p className="text-xs text-gray-400">Пока нет</p>}
            {wo.items.map((i) => (
              <div key={i.id} className="text-sm flex justify-between py-0.5">
                <span>{i.workTypeCustom?.trim() ? i.workTypeCustom : i.workType}</span>
                <span className="text-gray-500">{i.hours != null ? `${i.hours} ч` : ''}</span>
              </div>
            ))}
            {!closed && (
              <div className="mt-2 space-y-2 bg-gray-50 rounded-xl p-2">
                <select value={workType} onChange={(e) => setWorkType(e.target.value)} className="input">
                  {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
                {workType === 'Другое' && <input value={workTypeCustom} onChange={(e) => setWorkTypeCustom(e.target.value)} className="input" placeholder="Опишите работу" />}
                <input value={hours} onChange={(e) => setHours(e.target.value)} className="input" placeholder="Время, ч (напр. 0.5)" inputMode="decimal" />
                <button disabled={addWork.isPending} onClick={() => addWork.mutate()} className="w-full bg-gray-800 text-white text-sm py-2 rounded-lg disabled:opacity-50">+ Добавить работу</button>
                {addWork.isError && <p className="text-red-500 text-xs">{errMsg(addWork.error)}</p>}
              </div>
            )}
          </section>

          <section>
            <h4 className="font-semibold text-sm mb-1">Запчасти</h4>
            {wo.parts.length === 0 && <p className="text-xs text-gray-400">Пока нет</p>}
            {wo.parts.map((p) => (
              <div key={p.id} className="text-sm flex justify-between py-0.5">
                <span>{p.partName} × {p.quantity} {p.unit}</span>
                <span className="text-gray-500">{p.totalCost} ₽</span>
              </div>
            ))}
            {!closed && (
              <div className="mt-2 space-y-2 bg-gray-50 rounded-xl p-2">
                <select value={partId} onChange={(e) => selectPart(e.target.value)} className="input">
                  <option value="">— запчасть —</option>
                  {partsData?.parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {partId && (
                  <>
                    {ownQty > 0 ? (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setPartSource('own')} className={`flex-1 text-xs py-2 rounded-lg border ${partSource === 'own' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}>
                          Из подотчёта ({ownQty} {selPart?.unit})
                        </button>
                        <button type="button" onClick={() => setPartSource('point')} className={`flex-1 text-xs py-2 rounded-lg border ${partSource === 'point' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'}`}>
                          С точки
                        </button>
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-500">В подотчёте нет — списываем с точки</p>
                    )}
                    {partSource === 'point' && (
                      pointsWithStock.length > 0 ? (
                        <select value={partPointId} onChange={(e) => setPartPointId(e.target.value)} className="input">
                          <option value="">— точка списания —</option>
                          {pointsWithStock.map((s) => <option key={s.pointId} value={s.pointId}>{s.pointName}: {s.quantity} {selPart?.unit}</option>)}
                        </select>
                      ) : (
                        <p className="text-[11px] text-amber-600">Этой запчасти нет ни на одной точке</p>
                      )
                    )}
                  </>
                )}
                <input value={partQty} onChange={(e) => setPartQty(e.target.value)} className="input" placeholder="Кол-во (можно дробное)" inputMode="decimal" />
                <button disabled={!partId || (partSource === 'point' && !partPointId) || addPart.isPending} onClick={() => addPart.mutate()} className="w-full bg-gray-800 text-white text-sm py-2 rounded-lg disabled:opacity-50">+ Списать запчасть</button>
                {addPart.isError && <p className="text-red-500 text-xs">{errMsg(addPart.error)}</p>}
              </div>
            )}
          </section>

          {wo.totalCost != null && (
            <div className="text-sm bg-gray-50 rounded-xl p-2">
              Работа: {wo.laborCost ?? 0} ₽ · Запчасти: {wo.partsCost ?? 0} ₽ · <b>Итого: {wo.totalCost} ₽</b>
            </div>
          )}

          {!closed && (
            <button disabled={closeWo.isPending} onClick={() => closeWo.mutate()} className="w-full bg-green-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50">
              {closeWo.isPending ? 'Закрываю…' : 'Закрыть наряд'}
            </button>
          )}
        </div>
      )}
    </Overlay>
  )
}

function Overlay({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 max-h-[90vh] overflow-auto space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{title}</h3>
          <button onClick={onClose} className="text-gray-400 text-xl leading-none px-2">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      {children}
    </div>
  )
}
